import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { FirebaseService } from 'services/firebase.service';
import { LoggingService } from 'services/logging.service';
import { describeError } from '@cf-shared/error-describe';
import { CliAuthPageParams } from './cli-auth-interface';
import { CliAuthApproveResult, CliAuthSessionStatus, CliAuthStatusResult } from '@cf-shared/cli-auth-interface';

enum PageState {
  Ready,
  NotSignedIn,
  NoSession,
  Approving,
  Approved,
  Denied,
  Error,
}

@Component({
  selector: 'page-cli-auth',
  templateUrl: 'cli-auth.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CliAuthPage extends CsPage<CliAuthPageParams> {
  protected LL = LL;
  protected PageState = PageState;
  protected state = PageState.Ready;
  // technical error text from the network layer, not translatable catalog text
  protected errorMessage: TranslatedString = untranslatedString('');
  protected userEmail = '';
  private sessionId = '';

  constructor(
    public navCtrl: NavController,
    private firebase: FirebaseService,
    private cd: ChangeDetectorRef,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    this.sessionId = this.navParams.data.session || '';

    if (!this.sessionId) {
      this.state = PageState.NoSession;
      this.cd.markForCheck();
      return;
    }

    const user = this.firebase.fbAuth.currentUser;
    if (!user) {
      this.state = PageState.NotSignedIn;
      this.cd.markForCheck();
      return;
    }

    this.userEmail = user.email || '';

    // check if session was already processed (e.g. reopened after deny/approve)
    try {
      const response = await fetch('/api/cliAuthStatus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: this.sessionId, peek: true }),
      });
      if (response.ok) {
        const result: CliAuthStatusResult = await response.json();
        if (this.viewDestroyed) return;

        if (result.status === CliAuthSessionStatus.Approved) {
          this.state = PageState.Approved;
          this.cd.markForCheck();
          return;
        }
        if (result.status === CliAuthSessionStatus.Denied) {
          this.state = PageState.Denied;
          this.cd.markForCheck();
          return;
        }
        if (result.status === CliAuthSessionStatus.Expired) {
          this.state = PageState.NoSession;
          this.cd.markForCheck();
          return;
        }
      }
    } catch (err) {
      // if peek fails, fall through to Ready state. Approving then asks the server again,
      // so the person is not misled; report it so a broken status endpoint is noticed.
      this.logging.trackSilentProblem('CliAuthPeekFailed', { detail: describeError(err) });
    }

    if (this.viewDestroyed) return;
    this.state = PageState.Ready;
    this.cd.markForCheck();
  }

  onDenyClick() {
    void this.denyAsync();
  }

  private async denyAsync() {
    try {
      await fetch('/api/cliAuthDeny', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: this.sessionId }),
      });
    } catch (err) {
      // show denied state regardless, but the server never heard it, so the CLI keeps
      // waiting until the session expires: report it
      this.logging.trackSilentProblem('CliAuthDenyFailed', { detail: describeError(err) });
    }
    if (this.viewDestroyed) return;
    this.state = PageState.Denied;
    this.cd.markForCheck();
  }

  onAuthorizeClick(event: Event) {
    lockClick(event, this.authorizeAsync());
  }

  private async authorizeAsync() {
    this.state = PageState.Approving;
    this.cd.markForCheck();

    try {
      const user = this.firebase.fbAuth.currentUser;
      if (!user) {
        this.state = PageState.NotSignedIn;
        this.cd.markForCheck();
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch('/api/cliAuthApprove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ sessionId: this.sessionId, refreshToken: user.refreshToken }),
      });

      if (!response.ok) {
        this.state = PageState.Error;
        this.errorMessage = untranslatedString(`Server error (HTTP ${response.status})`);
        this.cd.markForCheck();
        return;
      }

      const result: CliAuthApproveResult = await response.json();
      if (this.viewDestroyed) return;

      if (result.error) {
        this.state = PageState.Error;
        this.errorMessage = untranslatedString(result.error);
      } else {
        this.state = PageState.Approved;
      }
    } catch (err: any) {
      this.state = PageState.Error;
      this.errorMessage = untranslatedString(err.message || 'Unknown error');
    }

    this.cd.markForCheck();
  }
}
